
import type { VueConstructor } from 'vue';
import Vue from 'vue';
import { mapGetters, mapState } from 'vuex';
import PublicPortfolioStrengthQuery from './apollo/PublicPortfolioStrength.gql';
import PublicPortfolioHeadlineShareButton from '@/components/partials/public-portfolio/PublicPortfolioHeadline/PublicPortfolioHeadlineShareButton/PublicPortfolioHeadlineShareButton.vue';
import PublicPortfolioHeadlineProfileStrengh from '@/components/partials/public-portfolio/PublicPortfolioHeadline/PublicPortfolioHeadlineProfileStrengh/PublicPortfolioHeadlineProfileStrengh.vue';
import { copyToClipboard } from '@/components/utils/browser/copy';
import { getPortfolioUrl } from '@/utils/routes';
import PublicPortfolioEmptyClientsTip from '~/components/partials/public-portfolio/PublicPortfolioTips/PublicPortfolioEmptyClientsTip.vue';
import PublicPortfolioEmptyHeadshotTip from '~/components/partials/public-portfolio/PublicPortfolioTips/PublicPortfolioEmptyHeadshotTip.vue';
import PublicPortfolioEmptyWorkhistoriesTip from '~/components/partials/public-portfolio/PublicPortfolioTips/PublicPortfolioEmptyWorkhistoriesTip.vue';
import PublicPortfolioEmptySalesExperienceTip from '~/components/partials/public-portfolio/PublicPortfolioTips/PublicPortfolioEmptySalesExperienceTip.vue';
import PublicPortfolioEmptyAwardsTip from '~/components/partials/public-portfolio/PublicPortfolioTips/PublicPortfolioEmptyAwardsTip.vue';
import PublicPortfolioReadyTip from '~/components/partials/public-portfolio/PublicPortfolioTips/PublicPortfolioReadyTip.vue';
import PublicPortfolioEmptyHeadlineTip from '~/components/partials/public-portfolio/PublicPortfolioTips/PublicPortfolioEmptyHeadlineTip.vue';
import PublicPortfolioEmptyTestimonialsTip from '~/components/partials/public-portfolio/PublicPortfolioTips/PublicPortfolioEmptyTestimonialsTip.vue';
import PublicPortfolioEmptyConnectionsTip from '~/components/partials/public-portfolio/PublicPortfolioTips/PublicPortfolioEmptyConnectionsTip.vue';
import type { ConnectionConnection, Testimonial } from '~/types/graphql';
import { TestimonialState } from '~/types/graphql';

type PublicPortfolioStrengthData = {
  publicPortfolioUser: {
    testimonials: Testimonial[];
    connections: Partial<ConnectionConnection>;
  };
};

const BASE_PROFILE_STRENGTH = 10;
const PROFILE_STEP_STRENGTH = 10;
const DESIRED_CONNECTIONS_COUNT = 10;
const PORTFOLIO_TOTAL_SECTION_COUNT = 9;

export default Vue.extend({
  name: 'PublicPortfolioHeadline',
  components: {
    PublicPortfolioHeadlineShareButton,
    PublicPortfolioHeadlineProfileStrengh,
  },
  apollo: {
    publicPortfolioUser: {
      query: PublicPortfolioStrengthQuery,
      variables(): { slug: string } {
        return {
          slug: this.slug,
        };
      },
    },
  },
  data(): PublicPortfolioStrengthData {
    return {
      publicPortfolioUser: {
        testimonials: [],
        connections: {
          edges: [],
        },
      },
    };
  },
  computed: {
    ...mapGetters('portfolio', [
      'hasAvatar',
      'hasClients',
      'hasEnoughClients',
      'hasDescription',
      'hasWorkHistories',
      'hasSalesExperience',
      'hasAwards',
    ]),
    ...mapState('portfolio', ['configuration']),
    slug(): string {
      return this.$route.params.slug;
    },
    url(): string {
      return getPortfolioUrl(this.slug);
    },
    hasVerifiedTestimonials(): boolean {
      return this.publicPortfolioUser.testimonials.filter(t => t.state === TestimonialState.Verified).length > 0;
    },
    hasEnoughConnections(): boolean {
      return (
        !!this.publicPortfolioUser.connections.edges &&
        this.publicPortfolioUser.connections.edges.length > DESIRED_CONNECTIONS_COUNT
      );
    },
    profileStrength(): number {
      const filledSectionsCount = [
        this.hasAvatar,
        this.hasClients,
        this.hasEnoughClients,
        this.hasDescription,
        this.hasWorkHistories,
        this.hasSalesExperience,
        this.hasAwards,
        this.hasVerifiedTestimonials,
        this.hasEnoughConnections,
      ].filter(Boolean).length;

      return filledSectionsCount === PORTFOLIO_TOTAL_SECTION_COUNT
        ? 100
        : filledSectionsCount * PROFILE_STEP_STRENGTH + BASE_PROFILE_STRENGTH;
    },
    tipComponent(): VueConstructor<Vue> {
      if (!this.hasEnoughClients) return PublicPortfolioEmptyClientsTip;
      if (!this.hasAvatar) return PublicPortfolioEmptyHeadshotTip;
      if (!this.hasDescription) return PublicPortfolioEmptyHeadlineTip;
      if (!this.hasWorkHistories) return PublicPortfolioEmptyWorkhistoriesTip;
      if (!this.hasSalesExperience) return PublicPortfolioEmptySalesExperienceTip;
      if (!this.hasAwards) return PublicPortfolioEmptyAwardsTip;
      if (!this.hasVerifiedTestimonials) return PublicPortfolioEmptyTestimonialsTip;
      if (!this.hasEnoughConnections) return PublicPortfolioEmptyConnectionsTip;

      return PublicPortfolioReadyTip;
    },
  },
  methods: {
    async copyToClipboard(): Promise<void> {
      try {
        await copyToClipboard(this.url);
        this.$notice.success('The link has been copied to clipboard');
      } catch (e) {
        this.$notice.error('Something went wrong', e);
        this.$sentryUtils.error(e);
      }
    },
  },
});
